import uniq from 'lodash/uniq';
import { sortLexicographically } from '../../lib/sortLexicographically';
import { RecordTypesLabelsByNameAndObjectName } from './types';
import {
  LayoutData,
  ParsedRecordType,
  ParsedRule,
  RelatedObjectData,
  RuleType,
} from '../parser/ParserTypes';
import {
  USED_UNIQUE_BY_RT,
  getDataFromRecordTypeIdWithLeadingFieldName,
} from '../parser/parserUtils';
import flatten from 'lodash/flatten';
import { ConfigurationToName, ConfigurationType, UnclassifiedTypes } from './dependencies/types';
import { ConfigurationInstanceData } from './dependencies/DependenciesTypes';
import { ANY_STEP_ID, ANY_STEP_NAME, getDisplayLineTexts } from '../Automations/helper';
import { getDataFromSFFieldName } from '../pages/rollups/rollupHelpers';
import { colors } from '@sweep-io/sweep-design';
import { Tag } from '../../apis/facades/useUserInputsApiFacade';
import { telemetry } from '../../telemetry';
import { AutomationType } from '../../types/enums/AutomationType';
import { RollupForTable } from '../pages/rollups/rollupTypes';
import { ConfigurationItem, FieldMetadataRecordProperties } from './dependencies/DependenciesTypes';
import { UserInfo } from '../../types/UserInfoTypes';
import pluralize from 'pluralize';
import { getObjectTypeColor } from '../sweep-canvas/helpers/getObjectTypeColor';
import groupBy from 'lodash/groupBy';
import { FunnelFieldLabels } from '../../constants/deprecated_fieldsManagementConsts';
import { formatCamelCase } from './universal-search/utils';
import { SweepFieldSummary } from '../../types/fields';
import { uniquePrimitives } from '../../lib/uniqueByKey';
import { AutomationTypes } from '../../types/enums/AutomationTypes';
import { createRecordTypeIdentifier, createStepId } from './pills/pillsUtils';

export const getObjectApiNamesFromFunnelMap = (funnelsData?: FunnelsData) => {
  const funnels = Object.values(funnelsData ?? {});

  return uniq(
    sortLexicographically({
      items: funnels?.map((funnel) => funnel.recordType?.objectName) ?? [],
    }),
  );
};

export const getObjectApiNamesFromRecordTypes = (recordTypesData?: RecordTypesData) => {
  return flatten(Object.keys(recordTypesData ?? {}).map((key) => key.split('.')[0]));
};

export const displayCorrectDateFormat = (value?: string) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

export const OCCUPIED_SPACE_SINGLE_OBJECT_VIEW = '250px';
export const OCCUPIED_SPACE_MAIN_VIEW = '200px';
export const OCCUPIED_SPACE_SINGLE_OBJECT_VIEW_CANVAS = '300px';

export const mapMissingInternalIdToMetadataId = (item: any) => ({
  ...item,
  id: item.id === 'MissingInternalId' || !item.id ? item.metadataId : item.id,
  _oldId: item._oldId ?? item.id,
});

export const isItemActive = (item: any) =>
  item?.hasOwnProperty('isActive') ? item.isActive : true;

export const checkIfActive = (onlyActive: boolean, item: any) =>
  !onlyActive || (onlyActive && isItemActive(item));

export const translateTypeToHumanName = (type: string) => {
  if (!type) {
    return type;
  }
  return (
    ConfigurationToName[type as ConfigurationType] ??
    FunnelFieldLabels[type as SweepFieldTypes] ??
    formatCamelCase(type)
  );
};

export const ruleTypeToConfigurationType = (type: RuleType | 'apexClasses') => {
  switch (type) {
    case RuleType.apexTriggers:
      return ConfigurationType.apexTriggers;
    case RuleType.approvalProcesses:
      return ConfigurationType.approvalProcesses;
    case RuleType.workflowRules:
      return ConfigurationType.workflowRules;
    case RuleType.flows:
      return ConfigurationType.flows;
    case RuleType.processBuilderFlows:
      return ConfigurationType.processBuilderFlows;
    case RuleType.cpqData:
      return ConfigurationType.cpqData;
    case RuleType.layouts:
      return ConfigurationType.layouts;
    case RuleType.validationRules:
      return ConfigurationType.validationRules;
    case 'apexClasses':
      return ConfigurationType.apexClasses;
    default:
      telemetry.captureError('unknown type in ruleTypeToConfigurationType', { type });
      return type as unknown as ConfigurationType;
  }
};

export const automationTypeToConfigurationType = (type: AutomationType) => {
  switch (type) {
    case AutomationType.Alert:
      return ConfigurationType.sweepAlert;
    case AutomationType.Assignment:
      return ConfigurationType.sweepAssignment;
    case AutomationType.Dedupe:
      return ConfigurationType.sweepDedupe;
    case AutomationType.Matching:
      return ConfigurationType.sweepMatching;
    case AutomationType.ScheduledAssignment:
      return ConfigurationType.sweepScheduledAssignment;
    case AutomationType.Default:
      return ConfigurationType.sweepAutomation;

    case AutomationType.ScheduledReport:
    case AutomationType.ScheduledJob:
      //no documentation support yet
      break;

    default:
      telemetry.captureError(new Error('unknown type in automationTypeToConfigurationType'), {
        type,
      });
      break;
  }
};

export const createConfigurationItemFromTag = (tag: Tag): ConfigurationItem => ({
  id: tag.id,
  name: tag.name,
  label: tag.name,
  objectName: '',
  type: UnclassifiedTypes.tag,
  filename: '',
  isActive: true,
  color: (colors[tag.color as keyof typeof colors] as any)?.[100] ?? tag.color ?? colors.blue[100],
  parentType: UnclassifiedTypes.tag,
  metadataId: '', //not relevant for tags
  _oldId: '', //not relevant for tags
});

//TODO fix type
export const createConfigurationItemFromObject = (object: {
  name: string;
  filename: string;
  label: string;
  objectType: string;
  metadataId: string;
}): ConfigurationItem => ({
  id: object.objectType,
  name: object.objectType,
  label: object.label,
  objectName: object.objectType,
  type: ConfigurationType.objects,
  parentType: ConfigurationType.objects,
  color: getObjectNameColor(object.objectType),
  isActive: true,
  metadataId: object.metadataId,
  filename: object.filename,
  _oldId: '',
});

export const createConfigurationItemFromSweepField = (
  sweepField: SweepFieldSummary,
): ConfigurationItem => ({
  id: sweepField.id ?? '',
  name: getDataFromSFFieldName(sweepField?.sfFieldName)?.[1] ?? '',
  label: sweepField.sweepFieldName ?? '',
  objectName: sweepField.objectName,
  type: sweepField.fieldType,
  filename: '',
  isActive: true,
  color: getObjectNameColor(sweepField.objectName),
  parentType: ConfigurationType.fields,
  metadataId: '', //not relevant for sweep fields
  _oldId: '',
});

export const createConfigurationItemFromField = (
  field: FieldMetadataRecordProperties,
  rollups?: RollupForTable[],
): ConfigurationItem => {
  const rollup = rollups?.find(
    (rollup) => rollup.sfFieldName === field.name && field.objectName === rollup.objectName,
  );

  if (rollup) {
    return createConfigurationItemFromRollupField({
      id: rollup.rollupId,
      sweepField: rollup.rollupField,
      usage: field.usage,
      link: field.link,
    });
  }

  return {
    ...field,
    createdAt: displayCorrectDateFormat(field.annotations?.createdAt),
    createdBy: field.annotations?.createdBy,
    updatedAt: displayCorrectDateFormat(field.annotations?.changedAt),
    updatedBy: field.annotations?.changedBy,
    filename: field.filename,
    isActive: true,
    isRollup: !!rollups?.find(
      (rollup) => rollup.sfFieldName === field.name && field.objectName === rollup.objectName,
    ),
    color: getObjectNameColor(field.objectName),
    parentType: ConfigurationType.fields,
    objectApiNames: [field.objectName],
    metadataId: field.metadataId,
    _oldId: field.id,
  };
};

export const createConfigurationItemFromRollupField = ({
  id,
  sweepField,
  usage,
  link,
}: {
  id: string;
  sweepField: SweepField;
  usage?: number;
  link?: string;
}): ConfigurationItem => ({
  id,
  name: getDataFromSFFieldName(sweepField?.sfFieldName)?.[1] ?? '',
  label: sweepField.sweepFieldName ?? '',
  objectName: sweepField.objectName,
  type: sweepField.fieldType,
  description: sweepField.description,
  createdAt: displayCorrectDateFormat(sweepField?.createdAt),
  createdBy: sweepField?.createdBy,
  updatedAt: displayCorrectDateFormat(sweepField?.updatedAt),
  updatedBy: sweepField?.updatedBy,
  helpText: sweepField.helpText,
  isMandatory: sweepField.isRequired,
  formula: sweepField.properties.formula,
  usage,
  link,
  filename: '',
  isActive: true,
  isRollup: true,
  color: getObjectNameColor(sweepField.objectName),
  parentType: ConfigurationType.rollups,
  metadataId: id, //not relevant for rollups
  _oldId: id,
});

export const createConfigurationItemFromParsedRule = (rule: ParsedRule): ConfigurationItem => ({
  ...rule,
  ...mapMissingInternalIdToMetadataId(rule),
  createdAt: displayCorrectDateFormat(rule.annotations?.createdAt),
  createdBy: rule.annotations?.createdBy,
  updatedAt: displayCorrectDateFormat(rule.annotations?.changedAt),
  updatedBy: rule.annotations?.changedBy,
  isActive: isItemActive(rule),
  color: getObjectNameColor(rule.objectApiNames[0]),
  parentType: ruleTypeToConfigurationType(rule.type),
});

export const getRecordTypeNamesFromAutomation = (automation?: AutomationStructureNew) => {
  if (!automation) {
    return [];
  }

  const funnelRecordTypeName = automation.funnelRecordType?.name
    ? createRecordTypeIdentifier({
        recordTypeName: automation.funnelRecordType?.name ?? '',
        funnelId: automation.automationDetails?.funnelId ?? '',
        objectName: automation.objectName,
        leadingField: '',
      })
    : '';

  const rtNames =
    automation.automationDetails?.recordTypes?.map((rt) =>
      createRecordTypeIdentifier({
        recordTypeName: rt.name,
        funnelId: '',
        objectName: automation.objectName,
        leadingField: '',
      }),
    ) ?? [];

  return uniquePrimitives([funnelRecordTypeName, ...rtNames].filter((rtName) => rtName !== ''));
};

export const getDocumentationStepsFromAutomation = (
  automation: AutomationStructureNew,
  propToUse: '_stageId' | 'stageName',
): string[] => {
  const { funnelRecordType, objectName, automationDetails } = automation;
  if (automationDetails.triggerType === AutomationTypes.FieldUpdate) {
    return [];
  }

  const funnelRecordTypeName = funnelRecordType?.name ?? '';
  const funnelId = automationDetails.funnelId ?? '';

  const steps = [
    automationDetails.automationParams.stageEnter?.toStage?.[propToUse],
    automationDetails.automationParams.stageExit?.toStage?.[propToUse],
    automationDetails.automationParams.stageExit?.fromStage?.[propToUse],
    automationDetails.automationParams.timeInStep?.stage?.[propToUse],
  ];

  const filteredSteps = steps.filter(
    (nameOrId) =>
      !!nameOrId && nameOrId !== ANY_STEP_NAME && nameOrId !== ANY_STEP_ID && !!nameOrId,
  );

  const documentationSteps = filteredSteps.length
    ? filteredSteps.map((step) => {
        return createStepId({
          recordTypeName: funnelRecordTypeName,
          objectName,
          stepFullName: step ?? '',
          funnelId: funnelId,
          leadingField: '',
        });
      })
    : [];

  return documentationSteps;
};

export const createConfigurationItemFromAutomation = (
  automation?: AutomationStructureNew,
  sweepUsers?: UserInfo[],
): ConfigurationItem | undefined => {
  const stagesNames = automation
    ? getDocumentationStepsFromAutomation(automation, 'stageName')
    : [];
  const recordTypeNames = getRecordTypeNamesFromAutomation(automation);

  const updatedBy = sweepUsers?.find((user) => user.id === automation?.updatedById);
  const createdBy = sweepUsers?.find((user) => user.id === automation?.createdById);

  const conditionsCounter = automation?.automationDetails.when?.criteria?.criteria.length;
  const conditionsStr = conditionsCounter
    ? `\n(${conditionsCounter} ${pluralize('Condition')})`
    : '';

  const { actionText, triggerText } = automation ? getDisplayLineTexts(automation, true) : {};
  let _actionText = '';

  const components: string[] = [];

  actionText?.forEach((action) => {
    if (typeof action === 'string') {
      _actionText = _actionText === '' ? action : _actionText + ',\n' + action;
    } else {
      if (Array.isArray(action)) {
        components.push(...action.map((a) => a.toString()).flat());
      } else {
        components.push(action as string);
      }
    }
  });

  components.push(_actionText);

  const isAutomationDedupeAndMatching =
    automation?.type === AutomationType.Dedupe || automation?.type === AutomationType.Matching;

  return automation
    ? {
        id: automation.automationId,
        name: automation.name,
        label: automation.name,
        createdAt: displayCorrectDateFormat(automation.createdAt),
        createdBy: createdBy?.name ?? 'No data',
        updatedAt: displayCorrectDateFormat(automation.updatedAt),
        updatedBy: updatedBy?.name ?? 'No data',
        objectName: automation.objectName,
        type: automation.type,
        stagesNames,
        usedByRecordTypes: recordTypeNames,
        filename: '',
        isActive: automation.isActive,
        trigger: triggerText + conditionsStr,
        actions: isAutomationDedupeAndMatching
          ? automation.automationDetails.actions.map(
              (action) =>
                (action.actionParams as DeduplicationActionParams | MatchingActionParams)
                  .dmType as string,
            )
          : automation.automationDetails.actions.map(
              ({ actionType }) => actionType as AutomationActionType,
            ),
        actionsToRender: components,
        color: getObjectNameColor(automation.objectName),
        metadataId: automation.automationId,
        parentType:
          automationTypeToConfigurationType(automation.type) ?? ConfigurationType.sweepAutomation,
        _oldId: automation.automationId,
      }
    : undefined;
};

export const createConfigurationItemFromRecordType = (
  recordType?: ParsedRecordType,
): ConfigurationItem | undefined => {
  return recordType
    ? {
        id: recordType.id,
        name: recordType.name,
        label: recordType.label,
        createdAt: displayCorrectDateFormat(recordType.annotations?.createdAt),
        createdBy: recordType.annotations?.createdBy,
        updatedAt: displayCorrectDateFormat(recordType.annotations?.changedAt),
        updatedBy: recordType.annotations?.changedBy,
        objectName: recordType.objectApiName,
        type: ConfigurationType.recordTypes,
        link: recordType.link,
        filename: recordType.filename,
        isActive: isItemActive(recordType),
        color: getObjectNameColor(recordType.objectApiName),
        parentType: ConfigurationType.recordTypes,
        metadataId: recordType.metadataId,
        _oldId: '',
      }
    : undefined;
};

export const createConfigurationItemFromLayout = (
  layout?: LayoutData,
  objectName?: string,
): ConfigurationItem | undefined => {
  return layout
    ? {
        ...layout,
        createdAt: displayCorrectDateFormat(layout.annotations?.createdAt),
        createdBy: layout.annotations?.createdBy,
        updatedAt: displayCorrectDateFormat(layout.annotations?.changedAt),
        updatedBy: layout.annotations?.changedBy,
        type: ConfigurationType.layouts,
        filename: layout.filename,
        objectName,
        color: getObjectNameColor(objectName ?? ''),
        parentType: ConfigurationType.layouts,
        isActive: true,
        ...mapMissingInternalIdToMetadataId(layout),
      }
    : undefined;
};

export const createConfigurationItemsFromRelatedObjectsData = (
  relatedObjects: RelatedObjectData,
  rollups?: RollupForTable[],
) => {
  const { parsedRules, parsedRecordTypes, parsedFields } = relatedObjects;

  const newParsedRules =
    parsedRules
      ?.map((rule) => ({
        ...rule,
        parentType: rule.type,
      }))
      .map(createConfigurationItemFromParsedRule) ?? [];

  const newParsedRecordTypes =
    parsedRecordTypes?.map(createConfigurationItemFromRecordType).filter((item) => !!item) ?? [];

  const parsedFieldsWithReferenceObjects = Object.entries(parsedFields ?? {})
    .map(([key, values]) => {
      return values.map((value) => ({ ...value, referencedObjects: [key] }));
    })
    .flat();

  const groupSameFieldIds = groupBy(parsedFieldsWithReferenceObjects, 'id');
  const groupedFields: ConfigurationItem[] = [];

  Object.keys(groupSameFieldIds).forEach((key) => {
    const firstField = groupSameFieldIds[key][0];
    let newItem = createConfigurationItemFromField(firstField, rollups);

    if (groupSameFieldIds[key].length > 1) {
      groupSameFieldIds[key].forEach((item) => {
        newItem = {
          ...newItem,
          referencedObjects: item.referencedObjects
            ? uniq([...(newItem.referencedObjects ?? []), ...(item.referencedObjects ?? [])])
            : newItem.referencedObjects,
        };
      });
    }

    groupedFields.push(newItem);
  });

  //If ids are the same it means that configuration is used in more than one object
  const groupSameIds = groupBy([...newParsedRules, ...newParsedRecordTypes], 'id');
  const result: ConfigurationItem[] = groupedFields;

  Object.keys(groupSameIds).forEach((key) => {
    let newItem = groupSameIds[key][0];

    if (groupSameIds[key].length > 1) {
      groupSameIds[key].forEach((item) => {
        newItem = {
          ...newItem,
          objectApiNames: uniq([...(newItem.objectApiNames ?? []), ...(item.objectApiNames ?? [])]),
          referencedObjects: uniq([
            ...(newItem.referencedObjects ?? []),
            ...(item.referencedObjects ?? []),
          ]),
        };
      });
    }

    result.push(newItem);
  });

  return result;
};

export const createConfigurationItemFromMetadataType = (
  metadataType: ConfigurationInstanceData,
): ConfigurationItem => {
  return {
    ...metadataType,
    ...mapMissingInternalIdToMetadataId(metadataType),
    name: metadataType.name,
    label: metadataType.label,
    type: metadataType.type,
    updatedAt: displayCorrectDateFormat(metadataType.annotations?.changedAt),
    updatedBy: metadataType.annotations?.changedBy,
    createdAt: displayCorrectDateFormat(metadataType.annotations?.createdAt),
    createdBy: metadataType.annotations?.createdBy,
    isActive: isItemActive(metadataType),
    color: getObjectNameColor(metadataType.id),
    parentType: metadataType.type,
  };
};

export const addLayoutProperties = (
  item: ConfigurationItem,
  layout?: LayoutData,
): ConfigurationItem => ({
  ...item,
  buttons: layout?.buttons ?? item.buttons,
  createdAt: displayCorrectDateFormat(
    layout?.annotations?.createdAt ?? item.annotations?.createdAt,
  ),
  createdBy: layout?.annotations?.createdBy ?? item.annotations?.createdBy,
  updatedAt: displayCorrectDateFormat(
    layout?.annotations?.changedAt ?? item.annotations?.changedAt,
  ),
  updatedBy: layout?.annotations?.changedBy ?? item.annotations?.changedBy,
  relatedLists: layout?.relatedLists?.length ? layout.relatedLists : item.relatedLists,
  description: layout?.description ?? item.description,
  link: layout?.link ?? item.link,
  filename: item.filename,
  isActive: isItemActive(item),
  parentType: item.parentType ?? item.type,
});

export const getConfigurationItemWithRTAttribution = (
  item: ParsedRule,
  recordTypeNamesUsedInCanvas: RecordTypesLabelsByNameAndObjectName,
): ConfigurationItem => ({
  ...item,
  usedOnlyByRecordType: item.usedByRecordTypes
    ?.filter((name) => name.match(USED_UNIQUE_BY_RT))
    .map((name) => getDataFromRecordTypeIdWithLeadingFieldName(name)?.recordTypeName)
    .filter((n) => n !== ''),

  usedOnlyByRecordTypeLabels: item.usedByRecordTypes
    ?.filter((name) => name.match(USED_UNIQUE_BY_RT))
    .map((name) => {
      const { recordTypeName, objectApiName } =
        getDataFromRecordTypeIdWithLeadingFieldName(name) ?? {};
      const label = recordTypeName
        ? recordTypeNamesUsedInCanvas?.[objectApiName]?.[recordTypeName]
        : '';
      return label;
    })
    .filter((n) => n !== ''),
  color: getObjectNameColor(item.objectApiNames[0]),
  parentType: item.type,
  isActive: isItemActive(item),
  ...mapMissingInternalIdToMetadataId(item),
});

export const getFunnelsStepNames = ({
  funnelsData = {},
  recordTypesData = {},
  crmOrgId,
}: {
  funnelsData?: FunnelsData;
  recordTypesData?: RecordTypesData;
  crmOrgId: string;
}) => {
  const stepNamesPerObject: { [objectApiName: string]: string[] } = {};

  Object.values(funnelsData).forEach((funnel) => {
    if (funnel.crmOrgId !== crmOrgId) return; //don't show funnels from other orgs
    const objectApiName = funnel.funnelDetails.leadingObject.objectName;
    const recordTypeName = funnel.recordType?.name;
    const stepNames = funnel.funnelDetails.stages.map((stage) =>
      createStepId({
        recordTypeName: recordTypeName,
        objectName: objectApiName,
        stepFullName: stage.stageName,
        funnelId: funnel.id,
        leadingField: funnel.funnelDetails.leadingObject.fieldName,
      }),
    );
    const oldSteps = stepNamesPerObject[objectApiName] ?? [];

    stepNamesPerObject[objectApiName] = uniq([...stepNames, ...oldSteps]);
  });

  Object.values(recordTypesData).forEach((rt) => {
    const objectApiName = rt.objectName;
    const rtStepNames =
      rt.leadingField?.values.map((step) =>
        createStepId({
          recordTypeName: rt?.name,
          objectName: objectApiName,
          stepFullName: step.fullName,
          funnelId: '',
          leadingField: rt.leadingField?.name ?? '',
        }),
      ) ?? [];
    const oldSteps = stepNamesPerObject[objectApiName] ?? [];

    stepNamesPerObject[objectApiName] = uniq([...rtStepNames, ...oldSteps]);
  });

  return stepNamesPerObject;
};

export const getObjectNameColor = (objectName: string) => {
  const leadingColor = getObjectTypeColor(objectName)?.leadingColorName as keyof typeof colors;
  return (colors[leadingColor] as any)?.[100] ?? colors['blue'][100];
};
